import React, { useState, useEffect, useRef } from 'react';
import { useClubs } from '../../hooks/useClubs';

/**
 * Componente de búsqueda predictiva de clubes.
 * @param {Function} onSelect - Callback cuando se selecciona un club.
 * @param {string} placeholder - Texto del placeholder.
 * @param {string} initialValue - Valor inicial para el input.
 * @param {string} organizerId - (Opcional) ID del organizador para filtrar clubes.
 */
const ClubSearch = ({ onSelect, placeholder = "Buscar club de pádel...", initialValue = "", organizerId = null }) => {
    const [query, setQuery] = useState(initialValue);
    const [results, setResults] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const { clubs, searchClubs, loading } = useClubs(organizerId);
    const wrapperRef = useRef(null);

    // Sincronizar query con valor inicial (por ejemplo, tras carga asíncrona en el padre)
    useEffect(() => {
        if (initialValue && query === "") {
            setQuery(initialValue);
        }
    }, [initialValue]);

    // Cerrar el dropdown al hacer clic fuera
    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [wrapperRef]);

    const handleSelect = (club) => {
        setQuery(club.n);
        setResults([]);
        setIsOpen(false);
        if (onSelect) onSelect(club);
    };

    // Al cambiar la query (escritura manual)
    const handleInputChange = (e) => {
        const val = e.target.value;
        setQuery(val);
        
        const searchResults = searchClubs(val);
        setResults(searchResults.slice(0, 10));
        
        if (val.length >= 2 || (organizerId && val.length >= 0)) {
            setIsOpen(true);
        } else {
            setIsOpen(false);
        }
    };

    const handleFocus = () => {
        if (organizerId) {
            const searchResults = searchClubs(query);
            setResults(searchResults.slice(0, 10));
            setIsOpen(true);
        }
    };

    return (
        <div ref={wrapperRef} className="relative w-full">
            <div className="relative">
                <input
                    type="text"
                    value={query}
                    onChange={handleInputChange}
                    onFocus={handleFocus}
                    placeholder={placeholder}
                    className="w-full pl-4 pr-10 py-3 border-2 border-gray-100 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all font-bold"
                />
                
                {/* Clear Button */}
                {query && !loading && (
                    <button
                        onClick={() => {
                            setQuery('');
                            setResults([]);
                            setIsOpen(false);
                            if (onSelect) onSelect(null);
                        }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500 transition-colors p-1 rounded-full hover:bg-gray-100"
                        title="Borrar búsqueda"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-5 h-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                )}

                {loading && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <div className="animate-spin h-5 w-5 border-2 border-green-500 border-t-transparent rounded-full"></div>
                    </div>
                )}
            </div>

            {isOpen && results.length > 0 && (
                <ul className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                    {results.map((club, index) => (
                        <button
                            key={index}
                            onClick={() => handleSelect(club)}
                            className="w-full text-left px-4 py-3 hover:bg-primary-50 transition-colors flex flex-col gap-0.5"
                        >
                            <span className="font-bold text-slate-900 leading-tight">
                                {club.n}
                            </span>
                            <span className="text-xs text-slate-500 truncate font-medium">
                                📍 {club.d}
                            </span>
                        </button>
                    ))}
                </ul>
            )}

            {isOpen && query.length >= 2 && results.length === 0 && !loading && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl p-4 text-center text-gray-500">
                    No se encontraron clubes para "{query}"
                </div>
            )}
        </div>
    );
};

export default ClubSearch;
